/*
 *  Copyright © Microsoft Corporation. All rights reserved.
 */

import { ApolloProvider } from '@apollo/client';
import { Customizer } from '@fluentui/react/lib/Utilities';
import * as React from 'react';
import { BrowserRouter } from 'react-router-dom';

import { MobileRoutes, Routes } from '../common/Routes';
import { LogCategory, ScenarioName, StepName } from '../logger';
import { getFabricCustomizations } from '../styles/TeamsCustomizations';
import { getApolloClient } from '../utilities/ApolloClient';
import { getAppHost } from '../utilities/TeamsUtils';

export const App: React.FC = (): JSX.Element => {
    const [theme, setTheme] = React.useState<string>(getAppHost().getTheme());

    // Component Lifecycle
    // Register a handler to flush out theme change and triggers a rerendering immediately
    React.useEffect((): any => {
        const logger = getAppHost().getLogger();

        // Component mounted
        logger.logTraceInfo(LogCategory.AppRunning, 'App rendered');
        logger.markScenario(ScenarioName.AppRunning, StepName.AppExecutionStarted);
        getAppHost().registerOnThemeChangeHandler((theme: string): void => {
            setTheme(theme);
        });

        return (): void => {
            // Component will unmount
            logger.markScenarioComplete(ScenarioName.AppRunning);
        };
    }, []);

    // Log theme on theme change
    React.useEffect((): void => {
        const logger = getAppHost().getLogger();
        logger.logTraceInfo(LogCategory.AppRunning, `Rendered app host with theme: ${theme}.`);
    }, [theme]);

    const isMobile = getAppHost().isMobile();
    const fabricCustomizations = getFabricCustomizations(theme, isMobile);
    const routes = isMobile ? MobileRoutes : Routes;

    return (
        <ApolloProvider client={getApolloClient()}>
            <Customizer {...fabricCustomizations}>
                <BrowserRouter>{routes}</BrowserRouter>
            </Customizer>
        </ApolloProvider>
    );
};
